import { addFilter } from "@wordpress/hooks";
import withFlexItemControls from "../hoc/withFlexItemControls";

// 1. Add responsive flex attributes to all blocks
function addFlexItemAttributes(settings) {
	if (settings.name === "sfb/container") {
		return settings;
	}
	return {
		...settings,
		attributes: {
			...settings.attributes,
			// Desktop
			flexGrowDesktop: { type: "number", default: 1 },
			flexShrinkDesktop: { type: "number", default: 1 },
			flexBasisDesktop: { type: "string", default: "auto" },
			alignSelfDesktop: { type: "string", default: "auto" },
			orderDesktop: { type: "number", default: 0 },
			// Tablet
			flexGrowTablet: { type: "number" },
			flexShrinkTablet: { type: "number" },
			flexBasisTablet: { type: "string" },
			alignSelfTablet: { type: "string" },
			orderTablet: { type: "number" },
			// Mobile
			flexGrowMobile: { type: "number" },
			flexShrinkMobile: { type: "number" },
			flexBasisMobile: { type: "string" },
			alignSelfMobile: { type: "string" },
			orderMobile: { type: "number" },
		},
	};
}

addFilter(
	"blocks.registerBlockType",
	"sfb/flex-item-attributes",
	addFlexItemAttributes,
);

// 2. Add controls to editor
addFilter("editor.BlockEdit", "sfb/flex-item-controls", withFlexItemControls);

// 3. Add styles in editor (apply desktop by default, responsive handled by CSS)
function addFlexItemStyles(BlockListBlock) {
	return (props) => {
		const { attributes, name } = props;
		if (name === "sfb/container") {
			return <BlockListBlock {...props} />;
		}

		const {
			flexGrowDesktop = 1,
			flexShrinkDesktop = 1,
			flexBasisDesktop = "auto",
			alignSelfDesktop = "auto",
			orderDesktop = 0,
		} = attributes;

		const flexItemStyle = {
			flex: `${flexGrowDesktop} ${flexShrinkDesktop} ${flexBasisDesktop}`,
			alignSelf: alignSelfDesktop !== "auto" ? alignSelfDesktop : undefined,
			order: orderDesktop !== 0 ? orderDesktop : undefined,
		};

		return (
			<BlockListBlock
				{...props}
				wrapperProps={{
					...props.wrapperProps,
					style: { ...props.wrapperProps?.style, ...flexItemStyle },
				}}
			/>
		);
	};
}

addFilter("editor.BlockListBlock", "sfb/flex-item-styles", addFlexItemStyles);

// 4. Add responsive styles on frontend using CSS variables
function addFlexItemSaveProps(props, blockType, attributes) {
	if (blockType.name === "sfb/container") return props;

	const {
		flexGrowDesktop = 1,
		flexShrinkDesktop = 1,
		flexBasisDesktop = "auto",
		alignSelfDesktop = "auto",
		orderDesktop = 0,
		flexGrowTablet,
		flexShrinkTablet,
		flexBasisTablet,
		alignSelfTablet,
		orderTablet,
		flexGrowMobile,
		flexShrinkMobile,
		flexBasisMobile,
		alignSelfMobile,
		orderMobile,
	} = attributes;

	const cssVars = {};

	// Desktop
	if (
		flexGrowDesktop !== 1 ||
		flexShrinkDesktop !== 1 ||
		flexBasisDesktop !== "auto"
	) {
		cssVars[
			"--flex-desktop"
		] = `${flexGrowDesktop} ${flexShrinkDesktop} ${flexBasisDesktop}`;
	}
	if (alignSelfDesktop !== "auto")
		cssVars["--align-self-desktop"] = alignSelfDesktop;
	if (orderDesktop !== 0) cssVars["--order-desktop"] = orderDesktop;

	// Tablet
	if (
		flexGrowTablet !== undefined ||
		flexShrinkTablet !== undefined ||
		flexBasisTablet !== undefined
	) {
		const grow = flexGrowTablet ?? flexGrowDesktop;
		const shrink = flexShrinkTablet ?? flexShrinkDesktop;
		const basis = flexBasisTablet ?? flexBasisDesktop;
		cssVars["--flex-tablet"] = `${grow} ${shrink} ${basis}`;
	}
	if (alignSelfTablet) cssVars["--align-self-tablet"] = alignSelfTablet;
	if (orderTablet !== undefined) cssVars["--order-tablet"] = orderTablet;

	// Mobile
	if (
		flexGrowMobile !== undefined ||
		flexShrinkMobile !== undefined ||
		flexBasisMobile !== undefined
	) {
		const grow = flexGrowMobile ?? flexGrowTablet ?? flexGrowDesktop;
		const shrink = flexShrinkMobile ?? flexShrinkTablet ?? flexShrinkDesktop;
		const basis = flexBasisMobile ?? flexBasisTablet ?? flexBasisDesktop;
		cssVars["--flex-mobile"] = `${grow} ${shrink} ${basis}`;
	}
	if (alignSelfMobile) cssVars["--align-self-mobile"] = alignSelfMobile;
	if (orderMobile !== undefined) cssVars["--order-mobile"] = orderMobile;

	if (Object.keys(cssVars).length === 0) return props;

	// Convert CSS vars to inline style
	const cssVarString = Object.entries(cssVars)
		.map(([key, value]) => `${key}:${value}`)
		.join(";");

	// Handle existing style - might be string or object
	let existingStyle = props.style || "";

	if (typeof existingStyle === "object" && existingStyle !== null) {
		existingStyle = Object.entries(existingStyle)
			.map(([key, value]) => {
				const kebabKey = key.replace(
					/[A-Z]/g,
					(match) => `-${match.toLowerCase()}`,
				);
				return `${kebabKey}:${value}`;
			})
			.join(";");
	}

	const newStyle = existingStyle
		? `${existingStyle};${cssVarString}`
		: cssVarString;

	return { ...props, style: newStyle };
}

addFilter(
	"blocks.getSaveContent.extraProps",
	"sfb/flex-item-save-props",
	addFlexItemSaveProps,
);

/**
 * Registers a new block provided a unique name and an object defining its behavior.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-registration/
 */
import { registerBlockType } from "@wordpress/blocks";

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * All files containing `style` keyword are bundled together. The code used
 * gets applied both to the front of your site and to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import "./style.scss";
import "./editor.scss";

const containerIcon = (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		fill="currentColor"
		class="bi bi-border-all"
		viewBox="0 0 16 16"
	>
		<path d="M0 0h16v16H0zm1 1v6.5h6.5V1zm7.5 0v6.5H15V1zM15 8.5H8.5V15H15zM7.5 15V8.5H1V15z" />
	</svg>
);

/**
 * Internal dependencies
 */
import Edit from "./edit";
import save from "./save";
import metadata from "./block.json";

/**
 * Every block starts by registering a new block type definition.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-registration/
 */
registerBlockType(metadata.name, {
	/**
	 * @see ./edit.js
	 */
	icon: containerIcon,
	edit: Edit,
	save,
});
